import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Field, Form, Formik, useFormikContext } from "formik";
import clsx from "clsx";
import { AuthField, AuthHead, AuthLayout } from "@app/components/auth-layout";
import { IcGift, IcIdCard, IcKeyRound, IcMail, IcPhone, IcUser } from "@app/components/icons";
import { Spinner, btn, btnGhost, btnLg, btnPrimary } from "@app/components/ui";
import { useT } from "@app/contexts/preferences.context";
import AuthService from "@app/services/http/auth.service";
import { SignUpInitialValues, SignUpRequest } from "@app/types";
import { signUpValidationSchema } from "@app/validations";
import useBusy from "@core/hooks/use-busy.hook";
import { PathnameUrl } from "@app/constants";

const TERMS_URL = PathnameUrl.TERMS;

function Agree() {
  const t = useT();
  const { errors, touched } = useFormikContext<SignUpInitialValues>();
  const bad = !!touched.isConfirmed && !!errors.isConfirmed;

  return (
    <div className="flex flex-col gap-1">
      <label className={clsx("flex cursor-pointer items-start gap-2 text-[13px] leading-snug", bad ? "text-bad" : "text-muted")}>
        <Field
          type="checkbox"
          name="isConfirmed"
          value="true"
          className="mt-px h-4 w-4 flex-none cursor-pointer accent-[var(--brand)]"
        />
        <span>
          {t("Tôi đồng ý với")}{" "}
          <a className="font-semibold text-brand hover:underline" href={TERMS_URL} target="_blank" rel="noreferrer">
            {t("Điều khoản dịch vụ")}
          </a>{" "}
          {t("của PC Render")}
        </span>
      </label>
      {bad && <span className="text-[12px] font-medium text-bad">{t(errors.isConfirmed as string)}</span>}
    </div>
  );
}

/** Đăng ký: POST /auth/register như bản cũ, xong thì báo kiểm tra email để xác minh tài khoản */
function SignUp() {
  const t = useT();
  const { busy, run } = useBusy();
  const [searchParams] = useSearchParams();
  const [sentTo, setSentTo] = useState("");

  // Mã giới thiệu lấy từ link ?ref_code=... thì khoá ô, như bản cũ
  const refCode = searchParams.get("ref_code");

  const handleSubmit = (values: SignUpInitialValues) => {
    const signUpRequest: SignUpRequest = {
      username: values.username,
      fullName: values.fullName,
      password: values.password,
      email: values.email,
      phone: values.phone,
      ref_code: values.ref_code,
    };

    run("signup", () => AuthService.signUp(signUpRequest), () => setSentTo(values.email));
  };

  if (sentTo) {
    return (
      <AuthLayout wide>
        <div className="my-auto flex flex-col items-center gap-1.5 py-6 text-center">
          <div className="mb-2 grid h-14 w-14 place-items-center rounded-2xl bg-ok-soft text-ok">
            <IcMail className="h-[26px] w-[26px]" />
          </div>
          <h1 className="m-0 text-[22px] font-bold">{t("Xác minh tài khoản")}</h1>
          <p className="m-0 max-w-[420px] text-muted [overflow-wrap:anywhere]">
            {t("Chúng tôi đã gửi liên kết xác minh tới")}{" "}
            <b className="font-mono text-[12.5px] text-fg">{sentTo}</b>.{" "}
            {t("Bạn hãy kiểm tra email để xác minh tài khoản rồi đăng nhập.")}
          </p>
          <div className="mt-4 flex w-full max-w-[420px] flex-wrap justify-center gap-2.5 max-[480px]:[&>*]:flex-1">
            <a className={clsx(btn, btnGhost)} href="https://mail.google.com/mail" target="_blank" rel="noreferrer">
              <IcMail className="h-[18px] w-[18px]" />
              {t("Đến email")}
            </a>
            <Link className={clsx(btn, btnPrimary)} to="/login">
              {t("Đăng nhập")}
            </Link>
          </div>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout wide>
      <AuthHead title={t("Tạo tài khoản")} sub={t("Điền thông tin bên dưới để tạo tài khoản PC Render.")} />

      <Formik
        initialValues={{
          username: "",
          email: "",
          fullName: "",
          phone: "",
          password: "",
          confirmPassword: "",
          ref_code: refCode || "",
          isConfirmed: [],
        }}
        onSubmit={handleSubmit}
        validationSchema={signUpValidationSchema}
      >
        <Form className="flex flex-col gap-4" noValidate>
          <div className="grid grid-cols-2 gap-x-3 gap-y-4 max-[560px]:grid-cols-1">
            <AuthField
              name="username"
              label={t("Tên tài khoản")}
              icon={<IcUser />}
              placeholder={t("3–20 ký tự")}
              maxLength={20}
              autoComplete="username"
              autoFocus
            />
            <AuthField
              name="fullName"
              label={t("Họ tên")}
              icon={<IcIdCard />}
              placeholder={t("Nguyễn Văn A")}
              maxLength={50}
              autoComplete="name"
            />
            <AuthField
              name="email"
              label={t("Email")}
              icon={<IcMail />}
              type="email"
              placeholder="email@example.com"
              maxLength={50}
              autoComplete="email"
            />
            <AuthField
              name="phone"
              label={t("Số điện thoại")}
              icon={<IcPhone />}
              type="tel"
              placeholder="09xxxxxxxx"
              maxLength={12}
              autoComplete="tel"
            />
            <AuthField
              name="password"
              label={t("Mật khẩu")}
              icon={<IcKeyRound />}
              type="password"
              placeholder={t("Nhập mật khẩu")}
              maxLength={30}
              autoComplete="new-password"
            />
            <AuthField
              name="confirmPassword"
              label={t("Nhập lại mật khẩu")}
              icon={<IcKeyRound />}
              type="password"
              placeholder={t("Nhập lại mật khẩu")}
              maxLength={30}
              autoComplete="new-password"
            />
          </div>
          <AuthField
            name="ref_code"
            label={t("Mã giới thiệu")}
            icon={<IcGift />}
            placeholder={t("Bỏ qua nếu không có")}
            maxLength={32}
            readOnly={!!refCode}
            optional={!refCode}
          />
          <Agree />
          <button type="submit" className={clsx(btn, btnPrimary, btnLg, "w-full")} disabled={busy.signup}>
            {busy.signup && <Spinner />}
            {t("Đăng ký")}
          </button>
        </Form>
      </Formik>

      <p className="mb-0 mt-6 border-t border-line pt-5 text-center text-[13.5px] text-muted">
        {t("Bạn đã có tài khoản?")}{" "}
        <Link className="font-semibold text-brand hover:underline" to="/login">
          {t("Đăng nhập")}
        </Link>
      </p>
    </AuthLayout>
  );
}

export default SignUp;
